import React, { useEffect, useState } from 'react';
import { Alert, Button, Container, IconButton, Stack } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import unifiedApi from '../../utils/unifiedApi';
import { EMERGENCY_CONTACT_UPDATED_EVENT, hasEmergencyContact } from '../../utils/emergencyContact';

export const DISMISS_KEY = 'emergencyContactBannerDismissed';

const isDismissed = () => {
  try {
    return sessionStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
};

/** Prompts logged-in members without an emergency contact to add one. Dismissed per session. */
const EmergencyContactBanner = () => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const [userTenantId, setUserTenantId] = useState(null);
  const [missing, setMissing] = useState(false);
  const [dismissed, setDismissed] = useState(isDismissed);

  useEffect(() => {
    if (!isAuthenticated || dismissed) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const me = await unifiedApi.getMe();
        if (cancelled || !me?.userTenantId) return;
        setUserTenantId(me.userTenantId);
        setMissing(!hasEmergencyContact(me));
      } catch {
        // Non-critical prompt; ignore failures.
      }
    })();
    return () => { cancelled = true; };
  }, [isAuthenticated, dismissed]);

  useEffect(() => {
    const handleUpdated = () => setMissing(false);
    window.addEventListener(EMERGENCY_CONTACT_UPDATED_EVENT, handleUpdated);
    return () => window.removeEventListener(EMERGENCY_CONTACT_UPDATED_EVENT, handleUpdated);
  }, []);

  const profilePath = userTenantId ? `/player/${userTenantId}` : null;
  const onExcludedPage = location.pathname === profilePath || location.pathname.startsWith('/emergency-contact/');

  if (!isAuthenticated || dismissed || !missing || !profilePath || onExcludedPage) return null;

  const handleDismiss = () => {
    try {
      sessionStorage.setItem(DISMISS_KEY, '1');
    } catch {
      // Storage unavailable — still hide for this render tree.
    }
    setDismissed(true);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 2, px: { xs: 1, sm: 2 } }}>
      <Alert
        severity="warning"
        action={
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Button component={Link} to={profilePath} color="inherit" size="small" sx={{ whiteSpace: 'nowrap' }}>
              Add now
            </Button>
            <IconButton aria-label="Dismiss" color="inherit" size="small" onClick={handleDismiss}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        }
        sx={{ alignItems: 'center' }}
      >
        Please add an emergency contact to your profile so we know who to call if something happens on the field.
      </Alert>
    </Container>
  );
};

export default EmergencyContactBanner;
